import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';

const HIDDEN_KEYS = new Set([
  'passwordHash',
  'password_hash',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'token',
  'tokenHash',
  'token_hash',
]);

const ROLE_LABELS = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  care_officer: 'Care Officer',
  customer_care: 'Customer Care Agent',
  subscriber: 'Subscriber',
  dependant: 'Dependant',
};

const formatLabel = (key) => String(key || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, char => char.toUpperCase());

const isPlainObject = (value) => (
  value &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const formatPrimitive = (value) => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
  }
  return String(value);
};

const DetailValue = ({ value, depth = 0 }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-[#8A8580]">Not provided</span>;
    if (value.every(item => !isPlainObject(item) && !Array.isArray(item))) {
      return <span>{value.map(formatPrimitive).join(', ')}</span>;
    }
    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div key={index} className="rounded-lg border border-[#E8E6E3] bg-[#FAFAF7] p-3">
            <DetailValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    if (depth > 3) {
      return <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(value, null, 2)}</pre>;
    }
    const entries = Object.entries(value).filter(([key]) => !HIDDEN_KEYS.has(key));
    if (entries.length === 0) return <span className="text-[#8A8580]">Not provided</span>;
    return (
      <dl className="grid gap-2">
        {entries.map(([key, item]) => (
          <div key={key} className="grid gap-1 rounded-lg border border-[#E8E6E3] bg-white/70 p-3 sm:grid-cols-[160px_1fr]">
            <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-[#5A5A5A]">{formatLabel(key)}</dt>
            <dd className="min-w-0 break-words text-sm text-[#1A1A1A]">
              <DetailValue value={item} depth={depth + 1} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <span>{formatPrimitive(value)}</span>;
};

const DetailSection = ({ title, data }) => {
  if (!data || (isPlainObject(data) && Object.keys(data).length === 0)) return null;
  return (
    <section className="rounded-xl border border-[#E8E6E3] bg-white p-4">
      <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">{title}</h3>
      <div className="mt-4">
        <DetailValue value={data} />
      </div>
    </section>
  );
};

const AdminUserDetailModal = ({ userId, open, onOpenChange }) => {
  const { authApi } = useAuth();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open || !userId) return;
      setLoading(true);
      setError('');
      try {
        const response = await authApi.get(`/admin/users/${userId}/details`);
        if (mounted) setDetails(response.data);
      } catch (err) {
        if (mounted) setError(getApiErrorMessage(err, 'Unable to load user details.'));
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [authApi, open, userId]);

  const accountData = useMemo(() => {
    if (!details?.user) return null;
    const { profile, ...account } = details.user;
    return account;
  }, [details]);

  const displayName = details?.user?.displayName || details?.user?.display_name || details?.user?.email || 'User details';
  const roleLabel = ROLE_LABELS[details?.role || details?.user?.role] || formatLabel(details?.role || details?.user?.role || 'User');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto bg-[#FAFAF7]">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <DialogTitle className="font-serif text-2xl">{displayName}</DialogTitle>
            {details?.user?.role && <Badge className="bg-[#F5E8EA] text-[#9F1D35]">{roleLabel}</Badge>}
          </div>
          <DialogDescription>
            Account, profile, and KYC/onboarding information currently stored for this user.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-[#5A5A5A]">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading full record...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && details && (
          <div className="space-y-4">
            <DetailSection title="Account" data={accountData} />
            <DetailSection title="Profile" data={details.profile} />
            <DetailSection title="KYC / Onboarding Record" data={details.kyc} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdminUserDetailModal;
